import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import { formatMoney, parseMoney, toIsoDate } from '../format.js';
import { uuid } from '../uuid.js';
import { useAccounts, useContacts, useCreateInvoice } from '../api/hooks.js';
import type { CreateInvoice, InvoiceLineInput } from '../api/hooks.js';

/**
 * A draft invoice.
 *
 * A draft carries **no number** — one is allocated at issue, so deleting a
 * draft cannot leave a hole in a run the law requires to be unbroken. Nothing
 * here is posted; this form is freely editable and freely discardable, which is
 * the whole point of the draft stage.
 *
 * Line amounts are rounded to øre **before** they are summed, matching the
 * service: the customer sees the rounded line amounts, and a total that is not
 * their sum is a document that fails its own arithmetic.
 */

interface DraftLine {
  key: string;
  description: string;
  /** Free text as typed; parsed with parseMoney, which handles both separators. */
  quantity: string;
  unitPrice: string;
  accountCode: string;
  vatCode: string;
}

function emptyLine(): DraftLine {
  return {
    key: uuid(),
    description: '',
    quantity: '1',
    unitPrice: '',
    accountCode: '',
    vatCode: '',
  };
}

export interface InvoiceDraftFormProps {
  readonly opened: boolean;
  readonly onClose: () => void;
}

export function InvoiceDraftForm({ opened, onClose }: InvoiceDraftFormProps): ReactNode {
  const t = useT();
  const contacts = useContacts({ type: 'customer' });
  const accounts = useAccounts();
  const create = useCreateInvoice();

  const [contactId, setContactId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(new Date());
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);

  const accountOptions = useMemo(
    () =>
      (accounts.data?.items ?? [])
        .filter((account) => account.isActive && account.type === 'revenue')
        .map((account) => ({
          value: account.code,
          label: `${account.code} — ${account.name}`,
        })),
    [accounts.data],
  );

  const filled = lines.filter(
    (line) =>
      line.description.trim() !== '' &&
      parseMoney(line.quantity) !== null &&
      parseMoney(line.unitPrice) !== null,
  );

  /** Rounded per line, then summed — the order the service uses. */
  const subtotal = useMemo(
    () =>
      filled.reduce((total, line) => {
        const quantity = parseMoney(line.quantity) ?? new Decimal(0);
        const unitPrice = parseMoney(line.unitPrice) ?? new Decimal(0);
        return total.plus(
          quantity.times(unitPrice).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN),
        );
      }, new Decimal(0)),
    [filled],
  );

  function updateLine(key: string, patch: Partial<DraftLine>): void {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }

  function reset(): void {
    setContactId(null);
    setDate(new Date());
    setDueDate(null);
    setLines([emptyLine()]);
  }

  const canSave =
    filled.length > 0 && date !== null && dueDate !== null && !create.isPending;

  function handleSubmit(): void {
    if (date === null || dueDate === null) {
      return;
    }
    const body: CreateInvoice = {
      ...(contactId === null ? {} : { contactId }),
      date: toIsoDate(date),
      dueDate: toIsoDate(dueDate),
      lines: filled.map((line): InvoiceLineInput => {
        const quantity = parseMoney(line.quantity);
        const unitPrice = parseMoney(line.unitPrice);
        return {
          description: line.description.trim(),
          quantity: (quantity ?? new Decimal(0)).toFixed(4),
          unitPrice: (unitPrice ?? new Decimal(0)).toFixed(4),
          ...(line.accountCode.trim() === ''
            ? {}
            : { accountCode: line.accountCode.trim() }),
          ...(line.vatCode.trim() === '' ? {} : { vatCode: line.vatCode.trim() }),
        };
      }),
    };

    create.mutate(body, {
      onSuccess: (invoice) => {
        notifications.show({
          color: 'green',
          title: t('draft.created'),
          message: `${formatMoney(invoice.total)} — endnu ikke udstedt`,
        });
        reset();
        onClose();
      },
    });
  }

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title={t('draft.title')}>
      {/*
        noValidate: the browser's own constraint validation would block submit
        before the app sees it, and show its message in its own language.
      */}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Stack gap="md">
          <Group grow align="flex-start">
            <Select
              label={t('draft.customer')}
              placeholder={t('draft.customerPlaceholder')}
              searchable
              clearable
              value={contactId}
              onChange={setContactId}
              disabled={contacts.isPending}
              data={(contacts.data?.items ?? []).map((contact) => ({
                value: contact.id,
                label: contact.name,
              }))}
            />
            <DateInput
              label={t('draft.date')}
              valueFormat="DD.MM.YYYY"
              value={date}
              onChange={(value) => {
                setDate(value === null ? null : new Date(value));
              }}
            />
            <DateInput
              label={t('draft.dueDate')}
              valueFormat="DD.MM.YYYY"
              value={dueDate}
              onChange={(value) => {
                setDueDate(value === null ? null : new Date(value));
              }}
            />
          </Group>

          {contacts.isError && <ErrorMessage error={contacts.error} />}
          {accounts.isError && <ErrorMessage error={accounts.error} />}

          <Table.ScrollContainer minWidth={720}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('draft.col.description')}</Table.Th>
                  <Table.Th style={{ width: 90 }}>{t('draft.col.quantity')}</Table.Th>
                  <Table.Th style={{ width: 120 }}>{t('draft.col.unitPrice')}</Table.Th>
                  <Table.Th style={{ width: 200 }}>{t('journal.col.account')}</Table.Th>
                  <Table.Th style={{ width: 100 }}>{t('draft.col.vatCode')}</Table.Th>
                  <Table.Th style={{ width: 44 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((line) => (
                  <Table.Tr key={line.key}>
                    <Table.Td>
                      <TextInput
                        aria-label={t('draft.col.description')}
                        value={line.description}
                        onChange={(event) => {
                          updateLine(line.key, { description: event.currentTarget.value });
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        aria-label={t('draft.col.quantity')}
                        // inputMode, not type="number": a number input rejects
                        // the comma a Danish keyboard produces and silently
                        // drops what was typed.
                        inputMode="decimal"
                        value={line.quantity}
                        onChange={(event) => {
                          updateLine(line.key, { quantity: event.currentTarget.value });
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        aria-label={t('draft.col.unitPrice')}
                        inputMode="decimal"
                        value={line.unitPrice}
                        onChange={(event) => {
                          updateLine(line.key, { unitPrice: event.currentTarget.value });
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        aria-label={t('journal.col.account')}
                        placeholder={t('draft.accountPlaceholder')}
                        searchable
                        clearable
                        data={accountOptions}
                        value={line.accountCode === '' ? null : line.accountCode}
                        onChange={(value) => {
                          updateLine(line.key, { accountCode: value ?? '' });
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        aria-label={t('draft.col.vatCode')}
                        placeholder="S1"
                        value={line.vatCode}
                        onChange={(event) => {
                          updateLine(line.key, { vatCode: event.currentTarget.value });
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t('draft.removeLine')}
                        disabled={lines.length === 1}
                        onClick={() => {
                          setLines((current) =>
                            current.filter((candidate) => candidate.key !== line.key),
                          );
                        }}
                      >
                        ×
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group justify="space-between">
            <Button
              variant="subtle"
              onClick={() => {
                setLines((current) => [...current, emptyLine()]);
              }}
            >
              {t('draft.addLine')}
            </Button>
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {t('draft.subtotal')}
              </Text>
              <Text className="money-cell" fw={600}>
                {formatMoney(subtotal)}
              </Text>
            </Group>
          </Group>

          <Text size="sm" c="dimmed">
            {t('draft.vatNote')}
          </Text>

          {create.isError && (
            <ErrorMessage error={create.error} title={t('draft.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!canSave}>
              {t('draft.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
